'use client';

import React, { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { addRegionPrefix } from 'components/shared/CustomLink';
import { useModal } from 'components/shared/Modal';
import { PLCTA } from 'components/performance-lab/PLCTA';
import { PlansCompareModal } from '../../PlansCompareModal';
import { PerformanceLabModalBodyScrollLock } from '../../PerformanceLabModalBodyScrollLock';
import { usePerformanceLabsIndia } from '../../buy/india/PerformanceLabsIndiaContext';
import type { ComparePlansOrigin } from '../../buy/india/types';
import { FESTIVE_PANELS, festiveCheckoutPath } from '../festiveOffer';

/**
 * Opens the pricing-page compare modal for a festive panel (diagnostics /
 * recovery), with a gift CTA per plan of that panel under the table.
 */
export const useOpenFestiveCompare = () => {
  const t = useTranslations('performance-lab');
  const modal = useModal();
  const router = useRouter();
  const { regionSlug } = useRegionLocale();
  const { sections } = usePerformanceLabsIndia();

  return useCallback(
    (sectionId: 'diagnostics' | 'recovery') => {
      const section = sections.find((entry) => entry.id === sectionId);
      if (!section?.compareData?.length || !section.compareHeaders?.length) {
        return;
      }

      const compareOrigin: ComparePlansOrigin | undefined =
        sectionId === 'recovery' ? 'recovery' : undefined;
      const giftCards =
        FESTIVE_PANELS.find((panel) => panel.key === sectionId)?.cards ?? [];
      const close = () => {
        modal.hide();
        modal.clear();
      };
      // A button, not a link: the modal must close before the route changes.
      const gift = (cardId: string) => {
        close();
        const path = festiveCheckoutPath(cardId);
        const href = addRegionPrefix(path, regionSlug);
        router.push(typeof href === 'string' ? href : path);
      };

      modal.set(
        <PerformanceLabModalBodyScrollLock>
          <PlansCompareModal
            headers={section.compareHeaders}
            categories={section.compareData}
            compareOrigin={compareOrigin}
            footer={giftCards.map((card) => (
              <PLCTA
                key={card.id}
                type="button"
                onClick={() => gift(card.id)}
                $plVariant="primary"
                className="pl-custom-type"
                data-buttontype={`performance_lab_festive_compare_gift_${card.id}`}
              >
                {t('festive.plans.ctaAria', { plan: t(card.nameKey) })}
              </PLCTA>
            ))}
            onClose={close}
          />
        </PerformanceLabModalBodyScrollLock>,
      );
      modal.setCloseBtnHidden(true);
      modal.setTop();
      modal.setPortal(true);
      modal.show();
    },
    [modal, regionSlug, router, sections, t],
  );
};
